import qc from './questions'

export default {
  DJToJS: {
    InputComponentFromType: {
      likert: 'LikertQuestionInput',
      mcq: 'MCQQuestionInput',
      open: 'OpenQuestionInput'
    },
    /**
     * @typedef {Object} PageItemDJ
     * @property {Number} id - Identifiant de l'item
     * @property {QuestionDJ|null} question - éventuel Identifiant de la question
     * @property {String} item_type - type de l'item: titre, text ou question
     * @property {String|null} text - texte de l'item de type titre ou text
     * @property {Number} order - numero d'ordre dans la page
     * @property {Number} page - Identifiant de la page contenant cet item
     *
     * @param {PageItemDJ} item
     * @param {Boolean} fillingQuestionnaire - add answer key for question item if true
     * while using user api to retrieve pages
     * @returns {PageItemAir}
     */
    convertFormItem (item, fillingQuestionnaire = false) {
      const { id, order, item_type: type } = item
      const formItem = { id, key: id, order, type }

      switch (type) {
        case 'title':
          Object.assign(formItem, {
            component: 'TitleInput',
            value: item.text.replace(/<h[1-2]>(.*)<\/h[1-2]>/, '$1'),
            extra: item.text.includes('<h1>') ? 'main' : 'secondary'
          })
          break

        case 'text':
          Object.assign(formItem, {
            component: 'RichTextInput',
            value: JSON.parse(item.text).text,
            extra: JSON.parse(item.text).label
          })
          break

        case 'question':
          Object.assign(formItem, {
            component: this.InputComponentFromType[item.question.question_type],
            value: item.question.id,
            extra: '',
            definition: JSON.parse(item.question.definition),
            ...item.question.question_type === 'likert'
              ? { scale: qc.DJToJS.likertScale(item.question.likert_scale) }
              : {}
          })
          if (fillingQuestionnaire) {
            Object.assign(
              formItem,
              {
                answer: !item.question.answer
                  ? { answer: null }
                  : item.question.question_type === 'likert'
                    ? {
                      ...item.question.answer,
                      answer: parseInt(item.question.answer.answer)
                    }
                    : item.question.answer
              }
            )
          }
          break

        default:
          throw new Error('Unknown item type')
      }
      return formItem
    },
    convertPage (page, fillingQuestionnaire = false) {
      return {
        id: page.id,
        pageNumber: page.number,
        questionnaireId: page.questionnaire,
        pageItems: page.page_items.map(item => this.convertFormItem(item, fillingQuestionnaire))
      }
    },
    /**
     * @typedef {Object} QuestionnaireDJ
     * @property {Number} id - Identifiant du questionnaire
     * @property {Number} pages_count - nombre de pages du questionnaire
     * @property {String} label - Label du questionnaire
     * @property {String} description - Description du questionnaire
     * @property {Boolean} is_published - Indique si le questionnaire est publié
     * @property {String} publication_date = Date de publication du questionnaire
     * @property {Date} created_at = Date de création du questionnaire
     * @property {Date} updated_at - Date de dernière modification du questionnaire
     * @property {String|Number} user - Gestionnaire du questionnaire
     * @property {Array} pages - Pages liées au questionnaire
     * @property {String} repository - Identifiant du référentiel associé
     *
     * @param questionnaire {QuestionnaireDJ}
     * @param withPages {Boolean} Indique si les pages sont présentes
     * @return {QuestionnaireAir | Object}
     */
    convertQuestionnaire (questionnaire, withPages) {
      let questionnaireJS = {
        id: null,
        label: '',
        description: '',
        publicationDate: null,
        isPublished: false,
        repository: null,
        pages: []
      }
      if (questionnaire.publication_date !== null) {
        questionnaireJS.publicationDate = Date.parse(questionnaire.publication_date)
      }

      questionnaireJS.id = questionnaire.id
      questionnaireJS.label = questionnaire.label
      questionnaireJS.description = questionnaire.description
      questionnaireJS.isPublished = questionnaire.is_published
      questionnaireJS.repository = questionnaire.repository

      withPages
        ? questionnaireJS.pages = questionnaire.pages.map(page => this.convertPage(page))
        : questionnaireJS.pageNumber = questionnaire.pages_count

      return questionnaireJS
    }
  },
  JSToDJ: {
    /**
     * @param item {PageItemAir}
     * @return {String}
     */
    convertText (item) {
      if (item.type === 'text') {
        return JSON.stringify({
          label: item.extra,
          text: item.value
        })
      } else {
        return item.extra === 'main'
          ? `<h1>${item.value}</h1>`
          : `<h2>${item.value}</h2>`
      }
    },
    /**
     * @typedef {Object} PageItemAir
     * @property id {String} Identifiant auto-généré pour l'objet
     * @property order {Number} Numéro d'ordre sur la page
     * @property component {String} Nom du composant utilisé pour créer l'objet
     * @property type {String} Valeur secondaire attachée à l'objet
     * @property value {String} Valeur attachée à l'objet
     * @property extra {String} Données secondaires attachées à l'élément
     * pour le type de titre ou pour le label des textes
     *
     * @param item {PageItemAir}
     * @param pageId {Number} ID de la page associée
     * @return {PageItemDJ}
     */
    convertFormItem (item, pageId) {
      return {
        item_type: item.type,
        order: item.order,
        page: pageId,
        ...item.type === 'question'
          ? { question: item.value }
          : { text: this.convertText(item) }
      }
    },
    /**
     * @param questionnaireJS {QuestionnaireAir}
     */
    convertQuestionnaireSettings (questionnaireJS) {
      return {
        label: questionnaireJS.label,
        description: questionnaireJS.description,
        publication_date: questionnaireJS.publicationDate,
        is_published: questionnaireJS.isPublished,
        repository: questionnaireJS.repository
      }
    },
    convertPage (pageJS) {
      return {
        id: pageJS.id,
        number: pageJS.pageNumber,
        questionnaire: pageJS.questionnaireId,
        page_items: pageJS.pageItems.map(item => this.convertFormItem(item, pageJS.id))
      }
    }
  }
}
